'use client';

import { useEffect, useRef } from 'react';
import styles from './LandingHome.module.css';
import { prefersReducedMotion, type BusSnapshot, type CampusSnapshot } from './landingData';

type Props = { bus: BusSnapshot; campus: CampusSnapshot; routes: BusSnapshot['routes']; active: boolean };

/** Busch ↔ Livingston corridor with every captured bus gliding along its real route. Animates only while shown. */
export default function BusMiniMap({ bus, campus, routes, active }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const runRef = useRef<(on: boolean) => void>(() => {});

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const ns = 'http://www.w3.org/2000/svg';
    const layer = svg.querySelector('[data-vehicles]');
    if (!layer) return;
    const colors = Object.fromEntries(routes.map((r) => [r.id, r.color]));
    const vehicles = bus.buses.flatMap((b) => {
      const segments = [...svg.querySelectorAll<SVGPathElement>(`path[data-route="${b.route}"]`)];
      if (!segments.length) return [];
      const path = segments.reduce((a, c) => (c.getTotalLength() > a.getTotalLength() ? c : a));
      const length = path.getTotalLength();
      const pts: Array<[number, number]> = [];
      for (let s = 0; s <= length; s += 3) {
        const q = path.getPointAtLength(s);
        pts.push([q.x, q.y]);
      }
      let pos = 0;
      let best = Infinity;
      pts.forEach(([x, y], i) => {
        const d = (x - b.x) ** 2 + (y - b.y) ** 2;
        if (d < best) { best = d; pos = i; }
      });
      const halo = document.createElementNS(ns, 'circle');
      halo.setAttribute('r', '13');
      halo.setAttribute('fill', colors[b.route]);
      halo.setAttribute('opacity', '.3');
      const dot = document.createElementNS(ns, 'circle');
      dot.setAttribute('r', '6.5');
      dot.setAttribute('fill', colors[b.route]);
      dot.setAttribute('class', styles.mapBus);
      layer.append(halo, dot);
      return [{ pts, pos, speed: 0.35 + Math.random() * 0.25, dot, halo }];
    });
    const draw = () => vehicles.forEach((v) => {
      const [x, y] = v.pts[Math.floor(v.pos)];
      v.dot.setAttribute('cx', String(x)); v.dot.setAttribute('cy', String(y));
      v.halo.setAttribute('cx', String(x)); v.halo.setAttribute('cy', String(y));
    });
    draw();
    let raf = 0;
    let last = 0;
    const tick = (now: number) => {
      const dt = Math.max(0, Math.min(64, now - last));
      last = now;
      vehicles.forEach((v) => { v.pos = (v.pos + (v.speed * dt) / 16) % v.pts.length; });
      draw();
      raf = requestAnimationFrame(tick);
    };
    runRef.current = (on) => {
      if (prefersReducedMotion()) return;
      if (on && !raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      if (!on && raf) { cancelAnimationFrame(raf); raf = 0; }
    };
    return () => {
      if (raf) cancelAnimationFrame(raf);
      layer.replaceChildren();
    };
  }, [bus, routes]);

  useEffect(() => { runRef.current(active); }, [active]);

  return (
    <div className={styles.busmap}>
      <svg ref={svgRef} viewBox="130 50 760 330" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <path d={campus.parking.join('')} className={styles.mapPrk} />
        <path d={campus.buildings.join('')} className={styles.mapBld} />
        {routes.map((r) => r.d.map((d, i) => (
          <g key={`${r.id}-${i}`}>
            <path d={d} className={styles.mapGlow} stroke={r.color} />
            <path d={d} className={styles.mapRoute} stroke={r.color} data-route={r.id} />
          </g>
        )))}
        {campus.campuses.filter((c) => c.name === 'Busch' || c.name === 'Livingston').map((c) => (
          <text key={c.name} x={c.x} y={c.y - 28} textAnchor="middle" className={styles.mapLabel}>{c.name}</text>
        ))}
        <g data-vehicles="" />
      </svg>
    </div>
  );
}
